import * as React from 'react';
import { ThemeProvider, createTheme, ScopedCssBaseline, GlobalStyles } from '@mui/material';

const mobileTheme = createTheme({
  palette: {
    primary: { main: '#FFD300' },
    secondary: { main: '#00C2A8' },
    background: { default: '#F6F6F6', paper: '#11182a', white: '#ffffff', black: '#00081C', grey: '#626366' },
    text: { primary: '#E6ECFF', secondary: '#626366' },
  },

  shape: { borderRadius: 14, borderRadius16: 16 },

  typography: {
    fontFamily: 'AA Sans, New Transport, sans-serif',
    button: { fontWeight: 600, textTransform: 'none' },
  },

  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 }
  },

  components: {
    MuiMenu: {
      styleOverrides: {
        list: {
          overflow: 'auto'
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderWidth: 1,
          borderColor: '#d9d9d9',
          width: '92%',
          marginBottom: '10px'
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.background.white,
          borderRadius: '8px',
          color: theme.palette.background.black,
        })
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontFamily: 'AA Sans',
          fontWeight: 700,
          fontSize: '20px',
          color: theme.palette.background.black,
          paddingBottom: 0
        })
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          display: 'flex',
          flexDirection: 'column',
          '& > :not(style) ~ :not(style)': {
            marginLeft: 0
          }
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          fontSize: '16px',
          color: '#626366'
        }
      }
    },
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: () => ({
          borderRadius: 4,
          paddingInline: 16,
          '&.cancel-btn': {
            borderRadius: '5px',
            width: '100%',
            height: '48px',
            fontSize: '18px',
            fontFamily: 'New Transport',
            fontWeight: 700,
            backgroundColor: '#ffffff',
            color: '#00081C',
            border: '2px solid #00081C'
          },
          '&.confirm-btn': {
            marginLeft: '0px !important',
            marginTop: '10px',
            borderRadius: '5px',
            width: '100%',
            height: '48px',
            fontSize: '18px',
            fontFamily: 'New Transport',
            fontWeight: 700,
            backgroundColor: '#FFD300',
            color: '#00081C'
          }
        }),
        containedPrimary: { /* special primary style */ }
      },
      variants: [
        {
          props: {
            variant: 'outlined'
          },
          style: ({ theme }) => ({
            borderWidth: '2px',
            color: theme.palette.background.black,
            borderColor: theme.palette.background.black,
            backgroundColor: 'background.white',
            fontSize: '18px',
            fontFamily: 'New Transport',
            fontWeight: 700,
            height: '48px',
            width: '100%'
          })
        },
        {
          props: {
            variant: 'yellow'
          },
          style: ({ theme }) => ({
            borderWidth: '2px',
            color: theme.palette.background.black,
            borderColor: 'transparent',
            backgroundColor: theme.palette.primary.main,
            fontSize: '18px',
            fontFamily: 'New Transport',
            fontWeight: 700,
            height: '48px',
            width: '100%'
          })
        },
        {
          props: {
            variant: 'green'
          },
          style: () => ({
            borderWidth: '2px',
            color: '#ffffff',
            borderColor: 'transparent',
            backgroundColor: '#06757F',
            fontSize: '18px',
            fontFamily: 'New Transport',
            fontWeight: 700,
            height: '48px',
            width: '100%'
          })
        },
        {
          props: { variant: 'status' },
          style: {
            borderRadius: 8,
            height: '40px',
            fontSize: '16px',
            backgroundColor: '#000000',
            color: 'background.white',
            paddingLeft: '25px',
            paddingRight: '25px',
            fontWeight: 700,
            fontFamily: 'New Transport'
          }
        }
      ]
    },

    MuiTextField: {
      defaultProps: { size: 'small', fullWidth: true },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: 'rgba(255,255,255,0.04)',
          ':hover fieldset': { borderColor: '#3b74ff' },
          borderRadius: '8px',
          borderColor: '#DCDDDE',
          color: '#2B2929',
          '&.status-select': {
            height: '72px',
            fontSize: '16px'
          },
          '&.comp-select': {
            height: '48px',
            fontSize: '16px',
            borderRadius: '4px',
            borderWidth: '1px',
            borderColor: '#626366'
          }
        },

        input: { paddingBlock: 10 }
      }
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '20px',
          color: '#00081C',
          fontWeight: 700,
          marginBottom: '10px'
        }
      }
    },

    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none', borderRadius: 16 } }
    },

    MuiTypography: {
      styleOverrides: {
        root: {
          '&.comp-errors': {
            fontSize: '16px',
            fontWeight: 400,
            fontFamily: 'New Transport',
            color: '#DA0076',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '0% 50%',
            paddingLeft: '20px',
            marginBottom: '10px',
            backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik05LjQwOTk1IDhMMTUuMDcgMi4zNEMxNS4zNTUzIDIuMDk1NjEgMTUuNDc5NiAxLjcxMTg5IDE1LjM5MTggMS4zNDY1OUMxNS4zMDM5IDAuOTgxMjg1IDE1LjAxODcgMC42OTYwNjYgMTQuNjUzNCAwLjYwODE5NEMxNC4yODgxIDAuNTIwMzIzIDEzLjkwNDMgMC42NDQ2MjggMTMuNjYgMC45MzAwMDRMNy45OTk5NSA2LjU5TDIuMzM5OTUgMC45MzAwMDRDMS45NDMxOSAwLjU5MDIyNyAxLjM1MTc2IDAuNjEzMDcyIDAuOTgyMzkgMC45ODI0NDFDMC42MTMwMjEgMS4zNTE4MSAwLjU5MDE3NiAxLjk0MzI0IDAuOTI5OTUzIDIuMzRMNi41ODk5NSA4TDAuOTI5OTUzIDEzLjY2QzAuNTQyMjI5IDE0LjA1IDAuNTQyMjI5IDE0LjY4IDAuOTI5OTUzIDE1LjA3QzEuMzE5OTkgMTUuNDU3NyAxLjk0OTkyIDE1LjQ1NzcgMi4zMzk5NSAxNS4wN0w3Ljk5OTk1IDkuNDFMMTMuNjYgMTUuMDdDMTQuMDUgMTUuNDU3NyAxNC42Nzk5IDE1LjQ1NzcgMTUuMDcgMTUuMDdDMTUuNDU3NyAxNC42OCAxNS40NTc3IDE0LjA1IDE1LjA3IDEzLjY2TDkuNDA5OTUgOFoiIGZpbGw9IiNEQTAwNzYiLz4KPC9zdmc+Cg==')"
          }
        }
      }
    }
  }
});

export function PatrolUITheme({ children }) {

  return (
    <ThemeProvider theme={mobileTheme}>
      <ScopedCssBaseline sx={{ width: '100%' }} enableColorScheme>
        <GlobalStyles styles={{ ':root': { '--radius-sm': '10px' } }} />

        {children}
      </ScopedCssBaseline>
    </ThemeProvider>
  );

}

